// Decision logic for the guest landing's "See my ballot" tap when no
// suggestion has been confirmed. Kept identical to mobile's
// src/utils/landingAddressSubmit.ts so both clients classify stuck submits with the
// same `reason` vocabulary in Mixpanel.

export type InvalidSubmitReason =
  | 'empty'
  | 'needs_street_address'
  | 'no_match'
  | 'lookup_failed'
  | 'unsupported_address'
  | 'prefill_failed';

/** Why a tapped suggestion couldn't be used. */
export type SelectionFailure = 'lookup_failed' | 'unsupported_address';

/** How a successful "Landing See Ballot Clicked" was reached. */
export type SeeBallotVia =
  'click' | 'auto_prefill' | 'awaited_selection' | 'resolved_text' | 'confirmed_match';

interface AddressComponentLike {
  types?: string[];
  shortText?: string;
  longText?: string;
  shortName?: string;
  longName?: string;
  short_name?: string;
  long_name?: string;
}

const TRAILING_ZIP = /\b\d{5}(?:-\d{4})?\s*$/;
const LEADING_NUMBER = /^\s*(\d+)/;
const ZIP_ONLY = /^\s*\d{5}(?:-\d{4})?\s*$/;

const componentValue = (components: unknown[] | undefined, type: string): string | undefined => {
  const c = (components as AddressComponentLike[] | undefined)?.find((x) =>
    x?.types?.includes(type)
  );
  return c?.shortText || c?.shortName || c?.short_name || c?.longText || c?.long_name;
};

export const looksLikeZip = (text: string): boolean => ZIP_ONLY.test(text);

/**
 * True when the text can't be a street address: a ZIP, a city, or
 * "City, ST 12345". Autocomplete only returns street addresses, so these
 * inputs get nonsense or no suggestions and would otherwise dead-end.
 */
export const needsStreetAddress = (text: string): boolean =>
  !/\d/.test(text.replace(TRAILING_ZIP, ''));

export type UnselectedSubmitStep = 'empty' | 'prefill_failed' | 'needs_street_address' | 'resolve';

/** What to do when "See my ballot" is tapped with no confirmed address. */
export const classifyUnselectedSubmit = ({
  text,
  prefillFailed = false,
}: {
  text: string;
  prefillFailed?: boolean;
}): UnselectedSubmitStep => {
  if (text.trim().length === 0) return 'empty';
  if (prefillFailed) return 'prefill_failed';
  if (needsStreetAddress(text)) return 'needs_street_address';
  return 'resolve';
};

/**
 * A text-resolved address is used without asking only when the typed house
 * number and ZIP both match it. Top-match ranking is biased by the server's
 * location, so a partial address can resolve to the right street in the wrong
 * state — anything less certain is shown back to the voter to confirm.
 */
export const isConfidentMatch = (
  typed: string,
  resolved: { address_components?: unknown[] }
): boolean => {
  const typedNumber = LEADING_NUMBER.exec(typed)?.[1];
  const rest = typedNumber ? typed.replace(LEADING_NUMBER, '') : typed;
  const typedZip = rest
    .match(/\b(\d{5})(?:-\d{4})?\b/g)
    ?.pop()
    ?.slice(0, 5);
  if (!typedNumber || !typedZip) return false;
  return (
    componentValue(resolved.address_components, 'street_number') === typedNumber &&
    componentValue(resolved.address_components, 'postal_code')?.slice(0, 5) === typedZip
  );
};

/**
 * Shape of the typed text for landing analytics. Never includes the text
 * itself, which is the voter's address.
 */
export const typedInputProperties = (text: string, suggestionsCount: number) => ({
  input_length: text.trim().length,
  suggestions_count: suggestionsCount,
  looks_like_zip: looksLikeZip(text),
});

/** Properties for "Landing See Ballot Invalid Submit". */
export const invalidSubmitProperties = (
  reason: InvalidSubmitReason,
  text: string,
  suggestionsCount: number
) => ({ reason, ...typedInputProperties(text, suggestionsCount) });

type Track = (event: string, properties?: Record<string, unknown>) => void;

/**
 * Landing signals between page load and a submit, each sent at most once per
 * visit (per lookup stage for errors), so a visitor who leaves without
 * submitting still shows how far they got.
 */
export const createLandingAddressFunnel = (track: Track) => {
  let focused = false;
  let suggestionsShown = false;
  const failedStages = new Set<string>();
  return {
    focused: () => {
      if (focused) return;
      focused = true;
      track('Landing Address Focused');
    },
    suggestionsChanged: (count: number) => {
      if (suggestionsShown || count === 0) return;
      suggestionsShown = true;
      track('Landing Address Suggestions Shown', { suggestions_count: count });
    },
    lookupFailed: (error: { stage: 'autocomplete' | 'details'; status: number | null }) => {
      if (failedStages.has(error.stage)) return;
      failedStages.add(error.stage);
      track('Landing Address Lookup Error', { stage: error.stage, status: error.status });
    },
  };
};
